import { useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useCallback } from "react";
import { sendLoggingEvents, TrackingEvents } from "../../events";
import { toast as baseToast } from "../../toasts";
import { controlAudioRecordingMutex as baseControlAudioRecordingMutex } from "../../video-sdk";
import {
  audioRecorderAtom,
  audioRecordingSilentRecoveryAttemptCountAtom,
} from "./state";
import { useAudioRecorderLifecycleCallbacks as useBaseAudioRecorderLifecycleCallbacks } from "./useAudioRecorderLifecycleCallbacks";
import { useStopRecordingCallback as useBaseStopRecordingCallback } from "./useStopRecordingCallback";

export const DEFAULT_MAX_AUDIO_RECORDING_SILENT_RECOVERY_ATTEMPTS = 2;

function promptUserToManuallyStartRecording(toast: typeof baseToast) {
  toast.error(
    "We've paused transcription due to issues recording the session. Please confirm that your browser has access to your microphone and try recording again.",
    { position: "top", key: "audio-recording-permissions-error" },
  );
}

interface UseHandleAudioRecordingRecoveryCallbackOptions {
  /**
   * Allows for dependency injection of the audio recorder lifecycle callbacks
   * for easier testing.
   */
  useAudioRecorderLifecycleCallbacks?: typeof useBaseAudioRecorderLifecycleCallbacks;
  /**
   * Allows for dependency injection of the stop recording callback for easier
   * testing.
   */
  useStopRecordingCallback?: typeof useBaseStopRecordingCallback;
  /**
   * Allows for dependency injection of the {@link baseToast} API for easier
   * testing.
   */
  toast?: typeof baseToast;
  /**
   * Allows for dependency injection of the
   * {@link baseControlAudioRecordingMutex} mutex for easier testing.
   */
  controlAudioRecordingMutex?: typeof baseControlAudioRecordingMutex;
}

/**
 * Handles resetting the audio recording environment in an attempt to recover
 * from audio playback permissions issues.
 *
 * After {@link DEFAULT_MAX_AUDIO_RECORDING_SILENT_RECOVERY_ATTEMPTS} silent
 * recovery attempts, the user will be prompted to manually restart recording.
 */
export function useHandleAudioRecordingRecoveryCallback({
  useAudioRecorderLifecycleCallbacks = useBaseAudioRecorderLifecycleCallbacks,
  useStopRecordingCallback = useBaseStopRecordingCallback,
  toast = baseToast,
  controlAudioRecordingMutex = baseControlAudioRecordingMutex,
}: UseHandleAudioRecordingRecoveryCallbackOptions = {}) {
  const stopRecording = useStopRecordingCallback();
  const setAudioRecordingSilentRecoveryAttemptCount = useSetAtom(
    audioRecordingSilentRecoveryAttemptCountAtom,
  );
  const getAudioRecordingSilentRecoveryAttemptCount = useAtomCallback(
    useCallback((get) => get(audioRecordingSilentRecoveryAttemptCountAtom), []),
  );

  const getAudioRecorder = useAtomCallback(
    useCallback((get) => get(audioRecorderAtom), []),
  );

  const { setUpRecording } = useAudioRecorderLifecycleCallbacks();

  const attemptSilentRecovery = useCallback(async () => {
    setAudioRecordingSilentRecoveryAttemptCount((count) => count + 1);
    sendLoggingEvents(
      TrackingEvents.RESETTING_AUDIO_RECORDER,
      { attemptCount: getAudioRecordingSilentRecoveryAttemptCount() },
      {
        logLevel: "info",
        message: "Attempting to silently reset audio recording environment",
      },
    );
    return await setUpRecording({ audioRecorder: getAudioRecorder() });
  }, [
    getAudioRecorder,
    getAudioRecordingSilentRecoveryAttemptCount,
    setAudioRecordingSilentRecoveryAttemptCount,
    setUpRecording,
  ]);

  const stopAndPromptUser = useCallback(async () => {
    await stopRecording({
      shouldBroadcastState: true,
      shouldTeardown: true,
      skipMutexLock: true,
    });
    setAudioRecordingSilentRecoveryAttemptCount(0);
    promptUserToManuallyStartRecording(toast);
  }, [setAudioRecordingSilentRecoveryAttemptCount, stopRecording, toast]);

  return useCallback(
    async ({
      maxSilentRecoveryAttempts = DEFAULT_MAX_AUDIO_RECORDING_SILENT_RECOVERY_ATTEMPTS,
    }: {
      maxSilentRecoveryAttempts?: number;
    } = {}) => {
      await controlAudioRecordingMutex.runExclusive(async () => {
        const recoveryAttemptCount =
          getAudioRecordingSilentRecoveryAttemptCount();
        if (recoveryAttemptCount < maxSilentRecoveryAttempts) {
          await attemptSilentRecovery();
        } else {
          await stopAndPromptUser();
        }
      }, "useHandleAudioRecordingRecoveryCallback");
    },
    [
      controlAudioRecordingMutex,
      getAudioRecordingSilentRecoveryAttemptCount,
      attemptSilentRecovery,
      stopAndPromptUser,
    ],
  );
}
